import { useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import * as Sentry from '@sentry/react';
import moment from 'moment';

import { Api } from 'src/models/Api.model';
import { CriteriaGroupGroupElementsInnerOperatorEnum } from 'src/models/GalenData/CriteriaGroupGroupElementsInner.model';
import { RetinalExamKeyFiledEnum } from 'src/models/GalenData/CustomDeviceDataModel.model';
import { DeviceDataView } from 'src/models/GalenData/DeviceDataView.model';
import { OrderData } from 'src/models/GalenData/Order.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import { I18NextNS } from 'src/models/Language.model';
import { Session, transformExamToSession } from 'src/models/Session.model';

import useCheckCameraConnection from 'src/hooks/useCheckCameraConnection';
import useGetRetinalExamDataModelConfig from 'src/hooks/useGetRetinalExamDataModelConfig';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';

import WarnNoConnectedCameraModal from '../custom_components/WarnNoConnectedCameraModal';

type MatchedFoundProps = {
  matchedPatient: GalenDataUser;
  openOrder: DeviceDataView<OrderData>;
  onGoBack: () => void;
  onResumedExam: (session: Session) => void;
};

export default function MatchedFound({
  matchedPatient,
  openOrder,
  onGoBack,
  onResumedExam,
}: MatchedFoundProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);

  const { deviceDataModelId } = useGetRetinalExamDataModelConfig();
  const { handleHttpRequestError } = useHandleHttpRequestError();

  const { isCameraConnected } = useCheckCameraConnection();

  const [isLoading, setIsLoading] = useState(false);
  const [
    shouldShowWarnNoConnectedCameraModal,
    setShouldShowWarnNoConnectedCameraModal,
  ] = useState(false);

  const orderDate = moment(openOrder.data.OrderStatusDatetime.value).format(
    'YYYY-MM-DD',
  );

  const resumeExistingExam = async (): Promise<void> => {
    try {
      const examResponse = await Api.remote.get_exams({
        deviceDataModelId,
        ownerFilter: {
          users: [matchedPatient.userId],
        },
        groupElements: [
          {
            key: RetinalExamKeyFiledEnum.IsHidden,
            operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
            value: false,
          },
          {
            key: RetinalExamKeyFiledEnum.OrderId,
            value: openOrder.data.OrderId.value,
            operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
          },
        ],
      });

      const exam = examResponse.data.content?.[0];
      if (!exam) {
        Sentry.captureMessage(
          `No exam found for order ID: ${openOrder.data.OrderId.value}`,
        );
        throw new Error(t('message.error.examNotFound'));
      }
      const session = transformExamToSession(exam);
      onResumedExam(session);
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: t('bootbox.title.Error'),
        message: errorMessage,
        locale: language,
        type: 'error',
      });
    }
  };

  const handleContinue = async () => {
    try {
      setIsLoading(true);
      await resumeExistingExam();
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: t('bootbox.title.Error'),
        message: errorMessage,
        locale: language,
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <p className="text-danger fw-bold fs-5">
        {t('patientFields.patientAlreadyExists')}
      </p>
      <Table>
        <thead>
          <tr>
            <th>{t('patientFields.DOB')}</th>
            <th>{t('patientFields.First Name')}</th>
            <th>{t('patientFields.Last Name')}</th>
            <th>{t('patientFields.PID')}</th>
            <th>{t('orderFields.dateCreated')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{matchedPatient.dateOfBirth}</td>
            <td>{matchedPatient.firstName}</td>
            <td>{matchedPatient.lastName}</td>
            <td>{matchedPatient.patientId}</td>
            <td>{orderDate}</td>
          </tr>
        </tbody>
      </Table>
      <div className="d-flex justify-content-between mt-3">
        <Button variant="primary-100" onClick={onGoBack} disabled={isLoading}>
          {t('pages:newExam.previous')}
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            if (!isCameraConnected) {
              setShouldShowWarnNoConnectedCameraModal(true);
              return;
            }
            handleContinue();
          }}
          disabled={isLoading}
        >
          {t('pages:newExam.startResumeExamOrder')}
        </Button>
      </div>
      <WarnNoConnectedCameraModal
        isVisible={shouldShowWarnNoConnectedCameraModal}
      />
    </div>
  );
}
